import React, { useEffect } from 'react';
import Link from '@dashboard/components/Link';
import ZapierLogo from './zapier.svg';
import RewardfulLogo from './rewardful.svg';
import ToltLogo from './tolt.png';
import Button from '@dashboard/components/Button';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import Features from '@dashboard/enums/features';
import SettingsContainer from '@dashboard/components/SettingsContainer';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import GoogleSheetIcon from '@dashboard/icons/GoogleSheetIcon';
import WebhookIcon from '@dashboard/icons/WebhookIcon';
import SettingsDescription from '@dashboard/components/SettingsDescription';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sellerGoogleKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';
import { useToast } from '@dashboard/context/ToastContext';
import Dialog from '@dashboard/components/Dialog';
import { CheckIcon } from '@heroicons/react/24/outline';
import { appUrl } from '@dashboard/services/env';
import { stringify } from 'query-string';
import useSellerQueries from '@/dashboard/hooks/queries/useSellerQueries';
import { prompt } from '@dashboard/lib/prompt';

const SettingsIntegrations = () => {
  return (
    <SettingsContainer size="4xl">
      <SettingsHeader id="integrations">Integrations</SettingsHeader>

      <SettingsDescription className="mb-4">
        Integrate your pages with other apps.
      </SettingsDescription>

      <div className="space-y-4">
        <div className="border-b border-gray-100 pb-4">
          <CanUseFeature feature={Features.GOOGLE_SHEETS}>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="bg-gray-50 border border-gray-200 p-8 rounded w-full sm:w-1/4 h-32 flex items-center">
                <GoogleSheetIcon className="h-full mx-auto" />
              </div>

              <div className="w-full sm:w-3/4 flex flex-col">
                <h4 className="font-semibold">Google Sheets</h4>
                <div className="text-gray-700">
                  Automatically add new orders to a Google Sheet.{' '}
                  <Link to="/help/articles/google-sheets-integration" target="_blank">
                    Learn more
                  </Link>
                </div>

                <div className="flex items-center justify-end gap-x-4 mt-auto">
                  <GoogleSheetsOAuth />
                </div>
              </div>
            </div>
          </CanUseFeature>
        </div>

        <div className="border-b border-gray-100 pb-4">
          <CanUseFeature feature={Features.WEBHOOKS}>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="bg-gray-50 border border-gray-200 p-8 rounded w-full sm:w-1/4 h-32 flex items-center">
                <WebhookIcon className="h-full mx-auto" />
              </div>

              <div className="w-full sm:w-3/4 flex flex-col">
                <h4 className="font-semibold">Webhooks</h4>
                <div className="text-gray-700">
                  Automatically send order data to your server or other endpoint whenever a new
                  order is placed.{' '}
                  <Link to="/help/articles/webhooks" target="_blank">
                    Learn more
                  </Link>
                </div>
              </div>
            </div>
          </CanUseFeature>
        </div>

        <div className="border-b border-gray-100 pb-4">
          <CanUseFeature feature={Features.ZAPIER}>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="bg-gray-50 border border-gray-200 p-8 rounded w-full sm:w-1/4 h-32 flex items-center">
                <img src={ZapierLogo} className="h-full mx-auto" />
              </div>

              <div className="w-full sm:w-3/4 flex flex-col">
                <h4 className="font-semibold">Zapier</h4>
                <div className="text-gray-700">
                  Connect with thousands of apps to automate your business tasks with our Zapier
                  integration. For example, add new customers to your CRM or to a mailing list when
                  you receive a new payment. No coding needed.{' '}
                  <Link to="/help/articles/integrate-with-zapier" target="_blank">
                    Learn more
                  </Link>
                </div>
                <div className="flex items-center justify-end gap-x-4 mt-auto">
                  <Button
                    to="https://zapier.com/apps/checkout-page/integrations"
                    target="_blank"
                    appearance="light"
                    external
                  >
                    Visit Zapier
                  </Button>
                </div>
              </div>
            </div>
          </CanUseFeature>
        </div>

        <div className="border-b border-gray-100 pb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="bg-gray-50 border border-gray-200 p-8 rounded w-full sm:w-1/4 h-32 flex items-center">
              <img src={RewardfulLogo} className="h-full mx-auto" />
            </div>

            <div className="w-full sm:w-3/4 flex flex-col">
              <h4 className="font-semibold">Rewardful</h4>
              <div className="text-gray-700">
                Rewardful is an easy way to setup affiliate and referral programs. Connect your
                checkout pages with Rewardful to track referrals and commissions.{' '}
                <Link to="/help/articles/rewardful-integration" target="_blank">
                  Learn more
                </Link>
              </div>
              <div className="ml-auto space-x-4 mt-auto">
                <Button
                  to="https://www.getrewardful.com/?via=checkoutpage"
                  target="_blank"
                  appearance="light"
                  external
                >
                  Visit Rewardful
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="border-b border-gray-100 pb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="bg-gray-50 border border-gray-200 p-8 rounded w-full sm:w-1/4 h-32 flex items-center">
              <img src={ToltLogo} className="h-full mx-auto py-3" />
            </div>

            <div className="w-full sm:w-3/4 flex flex-col">
              <h4 className="font-semibold">Tolt</h4>
              <div className="text-gray-700">
                Tolt is an all-in-one affiliate marketing software for SaaS startups. Launch your
                own affiliate program and track referrals seamlessly.{' '}
                <Link to="/help/articles/tolt-integration" target="_blank">
                  Learn more
                </Link>
              </div>
              <div className="ml-auto gap-x-2 mt-auto flex flex-row">
                <ToltEnableDisable />
              </div>
            </div>
          </div>
        </div>
      </div>
    </SettingsContainer>
  );
};

function ToltEnableDisable() {
  const { seller } = useAuth();
  const { updateSeller } = useSellerQueries();

  const isEnabled = seller.integrations?.tolt?.enabled;
  const publicId = seller.integrations?.tolt?.publicId;

  const handleToggle = async () => {
    if (isEnabled) {
      // Disable the integration
      updateSeller({ integrations: { tolt: { enabled: false } } });
    } else {
      // Enable the integration - prompt for program ID
      try {
        const enteredPublicId = await prompt('Enter your Tolt Public ID', {
          placeholder: 'Public ID (pk_xxx)',
          description: (
            <div>
              Grab your public ID (data-tolt ID) in the{' '}
              <Link to="https://app.tolt.io/settings?tab=integrations" external target="_blank">
                Tolt dashboard
              </Link>
            </div>
          ),
          confirmText: 'Enable',
          cancelText: 'Cancel',
          value: publicId,
        });

        if (enteredPublicId && enteredPublicId.trim()) {
          updateSeller({
            integrations: {
              tolt: {
                enabled: true,
                publicId: enteredPublicId.trim(),
              },
            },
          });
        }
      } catch (error) {
        // User cancelled or error occurred
        console.log('Tolt integration setup cancelled');
      }
    }
  };

  return (
    <div className="flex flex-row gap-x-2 items-center">
      {isEnabled && publicId && (
        <div className="flex gap-x-1 text-sm text-gray-600 items-center">
          <CheckIcon className="inline-block h-5 w-5 text-green-600" />
          Enabled{' '}
          <span className="text-xs">
            (
            <Link to={`https://app.tolt.io/program-settings`} target="_blank" external>
              {publicId}
            </Link>
            )
          </span>
        </div>
      )}
      <Button onClick={handleToggle} appearance="primary" size="small">
        {isEnabled ? 'Disable' : 'Enable'}
      </Button>
    </div>
  );
}

function GoogleSheetsOAuth() {
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();
  const [showDisconnectModal, setShowDisconnectModal] = React.useState(false);
  const { emitToast } = useToast();

  const { data: existingAccount, isLoading } = useQuery({
    queryKey: sellerGoogleKey(sellerId),
    queryFn: () => fetcher(`/oauth/google?sellerId=${sellerId}`, token),
    enabled: !!sellerId,
    retry: false,
  });

  const disconnectMutation = useMutation({
    mutationFn: () =>
      fetcher(`/oauth/google?sellerId=${sellerId}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.setQueryData(sellerGoogleKey(sellerId), null);
      queryClient.invalidateQueries({ queryKey: sellerGoogleKey(sellerId) });
      emitToast({
        title: 'Successfully disconnected Google account',
        type: 'success',
      });
      setShowDisconnectModal(false);
    },
    onError: () => {
      emitToast({
        title: 'Failed to disconnect Google account',
        type: 'error',
      });
    },
  });

  // creates oauth record from code
  const createOAuthMutation = useMutation({
    mutationFn: code => {
      return fetcher(`/oauth/google`, token, {
        method: 'POST',
        body: JSON.stringify({
          code,
          sellerId: sellerId,
          redirectURI: `${appUrl}/dashboard/integrations/google/callback`,
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sellerGoogleKey(sellerId) });
      emitToast({
        title: 'Successfully connected Google account',
        type: 'success',
      });
    },
    onError: () => {
      emitToast({
        title: 'Failed to connect Google account',
        type: 'error',
      });
    },
  });

  function connectNewAccount() {
    const endpointUrl = `https://accounts.google.com/o/oauth2/v2/auth?${stringify({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: `${appUrl}/dashboard/integrations/google/callback`,
      access_type: 'offline',
      response_type: 'code',
      prompt: 'consent',
      scope: [
        'https://www.googleapis.com/auth/spreadsheets',
        'https://www.googleapis.com/auth/userinfo.email',
      ].join(' '),
    })}`;

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;

    window.open(
      endpointUrl,
      'popup',
      `popup=true,width=${width},height=${height},left=${left},top=${top}`,
    );
  }

  useEffect(() => {
    const messageHandler = event => {
      if (event.origin !== appUrl) return;

      const { type, code } = event.data;

      if (type === 'google-sheets-oauth-callback' && code) {
        createOAuthMutation.mutate(code);
      }
    };

    window.addEventListener('message', messageHandler);

    return () => window.removeEventListener('message', messageHandler);
  }, []);

  if (isLoading) {
    return null;
  }

  if (!existingAccount) {
    return (
      <>
        <Button appearance="secondary" size="small" onClick={connectNewAccount}>
          Connect Google Account
        </Button>
      </>
    );
  }

  return (
    <>
      <div className="mt-4 flex items-center gap-x-2">
        <div className="text-sm text-gray-600">
          {existingAccount.config?.email ? (
            <div className="flex gap-x-1">
              <CheckIcon className="inline-block h-5 w-5 text-green-600" />
              <div>
                Connected to: <span className="font-medium">{existingAccount.config?.email}</span>
              </div>
            </div>
          ) : (
            <div className="flex gap-x-1">
              <CheckIcon className="inline-block h-5 w-5 text-green-600" />
              <div>Connected to Google</div>
            </div>
          )}
        </div>
        <Button appearance="secondary" size="small" onClick={() => setShowDisconnectModal(true)}>
          Disconnect
        </Button>
      </div>

      <Dialog
        title="Disconnect Google Account"
        open={showDisconnectModal}
        onCancel={() => setShowDisconnectModal(false)}
        cancelButton={{
          label: 'Cancel',
          appearance: 'light',
        }}
        confirmButton={{
          label: 'Disconnect account',
          appearance: 'primary',
          onClick: () => disconnectMutation.mutateAsync(),
          loading: disconnectMutation.isLoading,
        }}
      >
        <div className="text-gray-800">
          <p className="mb-2">
            Are you sure you want to disconnect your Google account? This will:
          </p>
          <ul className="list-disc ml-6 [&>li::marker]:text-gray-400 pl-4 space-y-0.5">
            <li className="pl-2 -ml-6">Stop sending new updates to spreadsheets</li>
            <li className="pl-2 -ml-6">Remove access to spreadsheets</li>
          </ul>
        </div>
      </Dialog>
    </>
  );
}

export default SettingsIntegrations;
